import { Box, Stack, ThemeProvider, Typography } from '@mui/material';
import { capitalize, map } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import Tooltip from '~/components/common/Tooltip';
import { DeliveryHours } from '~/models/user';
import { formatSimpleTime } from '~/utils/common';

type Props = {
  data: DeliveryHours;
};

const weekdays = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const ShiftSchedule = ({ data }: Props) => {
  const schedule = useMemo(
    () =>
      map(weekdays, (day) => {
        const result = data[day] || {
          available: false,
          all_hour: false,
          shifts: [],
        };

        let tooltipShift: React.ReactNode = 'Off shift';

        if (result.available) {
          if (result.all_hour) {
            tooltipShift = '24 hours';
          } else if (result.shifts.length > 0) {
            const rows = [];
            for (let i = 0; i < result.shifts.length; i += 2) {
              const pair = result.shifts.slice(i, i + 2).map((shift) => {
                const start = formatSimpleTime(
                  moment(shift.start_time, 'HH:mm'),
                );
                const end = formatSimpleTime(moment(shift.end_time, 'HH:mm'));
                return `${start} - ${end}`;
              });
              rows.push(pair.join(', '));
            }

            tooltipShift = (
              <Box>
                {rows.map((row) => (
                  <Typography key={row}>{row}</Typography>
                ))}
              </Box>
            );
          }
        }
        return {
          weekday: day,
          label: capitalize(day)[0],
          available: result.available,
          all_hour: result.all_hour,
          shifts: result.shifts,
          tooltipShift,
        };
      }),
    [data],
  );

  return (
    <Stack
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      {schedule?.map((day) => (
        <Box
          key={day.weekday}
          sx={{
            flex: 1,
            cursor: 'default !important',
          }}
        >
          <ThemeProvider
            theme={{
              components: {
                MuiTooltip: {
                  styleOverrides: {
                    tooltip: {
                      fontSize: 14,
                      backgroundColor: '#33363D',
                      padding: 4,
                      textAlign: 'left',
                      borderRadius: '4px 6px',
                      color: 'white',
                    },
                  },
                },
              },
            }}
          >
            <Tooltip title={day.tooltipShift} placement='top' sx={{}}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: !day.available ? '#E2E7F0' : '#2D91FC',
                    height: '4px',
                  }}
                />
                <Typography>{day.label}</Typography>
              </Box>
            </Tooltip>
          </ThemeProvider>
        </Box>
      ))}
    </Stack>
  );
};

export default ShiftSchedule;
